import { useEffect, useMemo, useState } from "react";
import { ResponsivePie } from "@nivo/pie";
import { tokens } from "../theme";
import { useTheme, Box, useMediaQuery, Typography } from "@mui/material";
import { getInventoryShare } from "../services/ProductService"; // new import

const InventoryShare = ({ isDashboard = false }) => {
  const theme = useTheme();
  // Use 'sm' breakpoint (<600px) so only mobile uses the flexbox layout
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery("(max-width:380px)"); // Targets very small phones (e.g. 320px wide)
  // memoize colors so the object reference is stable across renders
  const colors = useMemo(
    () => tokens(theme.palette.mode),
    [theme.palette.mode],
  );

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dynamic margin: Allocates a clean 160px gap on the right strictly for the legend on tablets/desktops
  const chartMargin = isMobile
    ? { top: 20, right: 20, bottom: 20, left: 20 }
    : { top: 40, right: 160, bottom: 40, left: 20 };

  const startAngle = isDashboard ? -90 : 0;

  // Legends: Desktop/Tablet use vertical SVG legend pushed fully into the right margin. Mobile uses custom HTML.
  const chartLegends = [
    {
      anchor: "right",
      direction: "column",
      translateX: 140, // Pushes the legend safely into the 160px right margin
      translateY: 0,
      itemsSpacing: 16,
      itemWidth: 100,
      itemHeight: 18,
      itemTextColor: colors.gray[100],
      itemDirection: "left-to-right",
      itemOpacity: 1,
      symbolSize: 18,
      symbolShape: "circle",
    },
  ];

  useEffect(() => {
    setLoading(true);
    // choose a sensible default limit depending on context
    const limit = isDashboard ? 5 : 10;
    getInventoryShare(limit)
      .then((resp) => {
        const payload = resp?.data ?? [];

        // Build a palette from theme tokens using all requested groups:
        // primary, blueAccent, redAccent, greenAccent, yellowAccent, gray
        const palette = [
          colors.greenAccent?.[600] ?? "#10b981",
          colors.blueAccent?.[500] ?? "#2563eb",
          colors.redAccent?.[500] ?? "#ef4444",
          colors.yellowAccent?.[800] ?? "#f59e0b",
          colors.primary?.[300] ?? "#2563eb",
          colors.gray?.[300] ?? "#9ca3af",
          // add fallback to repeat if more slices than colors
        ];

        // Map backend InventoryShareDTO { label, quantity } -> Nivo slice { id, label, value, color }
        const mapped = Array.isArray(payload)
          ? payload.map((it, idx) => {
              const qty =
                it?.quantity == null
                  ? 0
                  : typeof it.quantity === "number"
                    ? it.quantity
                    : // support strings (BigDecimal -> string) as well
                      Number(it.quantity.toString?.() ?? it.quantity) || 0;
              return {
                id: it.label ?? `Item ${idx + 1}`,
                label: it.label ?? `Item ${idx + 1}`,
                value: qty,
                color: palette[idx % palette.length],
              };
            })
          : [];

        // Defensive: if server returned no slices, keep empty state
        setData(mapped);
      })
      .catch((error) => {
        console.error("Error fetching inventory share:", error);
        setData([]);
      })
      .finally(() => setLoading(false));
  }, [isDashboard, colors]); // added colors to dependencies to fix hook warning

  // friendly empty state (keeps original component usage simple)
  if (loading) {
    return (
      <div
        style={{ textAlign: "center", padding: 20, color: colors.gray[100] }}
      >
        Loading inventory share...
      </div>
    );
  }

  // if there's no meaningful data, show a simple message (instead of rendering an empty pie)
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0);
  if (!data.length || total <= 0) {
    return (
      <div
        style={{ textAlign: "center", padding: 20, color: colors.gray[100] }}
      >
        No inventory data available
      </div>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
        <ResponsivePie
          data={data}
          startAngle={startAngle}
          theme={{
            // font/label customizations - make arc (slice) labels bigger & bolder via labels section
            labels: {
              text: {
                fill: colors.gray[100],
                fontSize: isExtraSmall ? 10 : isMobile ? 11 : 14,
                fontWeight: 700,
              },
            },
            legends: {
              text: {
                fill: colors.gray[100],
                fontSize: isMobile ? 10 : 12,
              },
            },
            tooltip: {
              container: {
                background: colors.primary[500],
                color: "#fff",
              },
            },
          }}
          margin={chartMargin}
          innerRadius={0.5}
          padAngle={0.7}
          cornerRadius={3}
          activeOuterRadiusOffset={8}
          borderWidth={1}
          borderColor={{
            from: "color",
            modifiers: [["darker", 0.2]],
          }}
          enableArcLinkLabels={false}
          arcLinkLabelsSkipAngle={10}
          arcLinkLabelsTextColor={colors.gray[300]}
          arcLinkLabelsThickness={2}
          arcLinkLabelsColor={{ from: "color" }}
          enableArcLabels={true}
          arcLabelsSkipAngle={10}
          // arcLabelsTextColor still uses darker modifier; actual displayed label is controlled by arcLabel prop below
          arcLabelsTextColor={{
            from: "color",
            modifiers: [["brighter", 4]],
          }}
          // Provide a custom arc label to show the raw value (formatted) and rely on theme.labels for size/weight.
          arcLabel={(d) => {
            const qty = Number(d.value) || 0;
            return qty.toLocaleString("en-NG");
          }}
          defs={[
            {
              id: "dots",
              type: "patternDots",
              background: "inherit",
              color: "rgba(255, 255, 255, 0.3)",
              size: 4,
              padding: 1,
              stagger: true,
            },
            {
              id: "lines",
              type: "patternLines",
              background: "inherit",
              color: "rgba(255, 255, 255, 0.92)",
              rotation: -45,
              lineWidth: 6,
              spacing: 10,
            },
          ]}
          // use the color supplied per datum
          colors={(d) => d.data.color}
          tooltip={({ datum }) => {
            const qty = Number(datum.value) || 0;
            // use the slice color for the value to match slice color
            const sliceColor =
              datum.color || datum.data?.color || colors.primary?.[500];

            return (
              <div
                style={{
                  padding: "8px 10px",
                  background: colors.primary[500],
                  color: "#fff",
                  borderRadius: 6,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                }}
              >
                <div style={{ fontWeight: 600 }}>{datum.id}</div>
                <div
                  style={{
                    marginTop: 6,
                    color: sliceColor,
                    fontWeight: 800,
                    fontSize: 16,
                  }}
                >
                  {qty.toLocaleString("en-NG")}
                </div>
              </div>
            );
          }}
          legends={isMobile ? [] : chartLegends}
        />
      </Box>

      {/* Custom Flexbox Legend for Mobile Devices - Auto wraps and never overflows */}
      {isMobile && (
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="center"
          alignItems="center"
          gap={isExtraSmall ? "8px 12px" : "10px 16px"}
          pt={1}
          pb={isDashboard ? 0 : 2}
        >
          {data.map((d) => (
            <Box key={d.id} display="flex" alignItems="center" gap="6px">
              <Box
                width={isExtraSmall ? "8px" : "10px"}
                height={isExtraSmall ? "8px" : "10px"}
                borderRadius="50%"
                bgcolor={d.color}
              />
              <Typography
                fontSize={isExtraSmall ? "10px" : "12px"}
                color={colors.gray[100]}
                fontWeight={500}
              >
                {d.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default InventoryShare;
